/**
 * WorkspacesPanel — per-device folder list + Sync action.
 *
 * Rendered on the Device Detail page, below the capability/info block. The
 * "Sync" button kicks a long-poll (`POST /workspaces/refresh`) at the hub,
 * which forwards a `device.workspaces.refresh` to the device and waits up to
 * 5s for a report. On timeout / offline device the hub returns `stale: true`
 * with the last-known list.
 *
 * Click a folder row → marks it as the active default for the next
 * dispatch (stored on the global UI store, keyed by deviceId). Click the
 * same row again to clear.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { GitBranch, RefreshCw, FolderOpen } from "lucide-react";
import type { Workspace } from "@paperclip-cloud/shared";
import { workspacesApi } from "../api/queries/workspaces";
import { toast } from "./Toast";
import { formatRelative } from "../lib/time";
import { useUiStore } from "../stores/ui";

export interface WorkspacesPanelProps {
  deviceId: string;
  /**
   * When true, clicking a row also fires `onPick` instead of just toggling.
   * The chat-panel folder picker uses this in its modal.
   */
  onPick?: (path: string | null) => void;
  /**
   * Hide the panel header when used inside another container (e.g. the
   * picker modal already has a title).
   */
  hideHeader?: boolean;
}

export function workspacesQueryKey(deviceId: string) {
  return ["device-workspaces", deviceId] as const;
}

export function WorkspacesPanel({
  deviceId,
  onPick,
  hideHeader,
}: WorkspacesPanelProps) {
  const qc = useQueryClient();
  const selected = useUiStore((s) => s.selectedWorkspaceByDevice[deviceId]);
  const setSelectedWorkspace = useUiStore((s) => s.setSelectedWorkspace);

  const q = useQuery({
    queryKey: workspacesQueryKey(deviceId),
    queryFn: () => workspacesApi.get(deviceId),
    refetchInterval: 30_000,
  });

  const refresh = useMutation({
    mutationFn: () => workspacesApi.refresh(deviceId),
    onSuccess: (res) => {
      qc.setQueryData(workspacesQueryKey(deviceId), {
        workspaces: res.workspaces,
      });
      qc.invalidateQueries({ queryKey: workspacesQueryKey(deviceId) });
      if (res.stale) {
        toast("Device offline — showing last known list", "error");
      } else {
        const n = res.workspaces?.workspaces.length ?? 0;
        toast(`Synced ${n} folder${n === 1 ? "" : "s"}`, "success");
      }
    },
    onError: (err: Error) =>
      toast(err.message ?? "Sync failed", "error"),
  });

  const report = q.data?.workspaces ?? null;

  const onRowClick = (w: Workspace) => {
    if (onPick) {
      // Picker mode: a click is a definitive pick (used inside the modal).
      setSelectedWorkspace(deviceId, w.path);
      onPick(w.path);
      return;
    }
    // Toggle mode: re-clicking the selected row unselects.
    if (selected === w.path) setSelectedWorkspace(deviceId, null);
    else setSelectedWorkspace(deviceId, w.path);
  };

  // Group workspaces by their containing root for display.
  const grouped = groupByRoot(report);

  return (
    <section className="card flex flex-col" aria-label="Workspaces">
      {!hideHeader && (
        <header className="flex items-center justify-between border-b border-border-subtle px-4 py-3">
          <div className="flex items-center gap-2">
            <FolderOpen size={14} className="text-tertiary" aria-hidden="true" />
            <h2 className="text-[13px] font-semibold tracking-[-0.005em] text-primary">
              Workspaces
            </h2>
            {report && (
              <span className="micro">
                Scanned {formatRelative(report.scanned_at)}
              </span>
            )}
          </div>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
            aria-label="Sync workspaces"
          >
            {refresh.isPending ? (
              <span className="spinner" aria-hidden="true" />
            ) : (
              <RefreshCw size={12} aria-hidden="true" />
            )}
            {refresh.isPending ? "Syncing…" : "Sync"}
          </button>
        </header>
      )}

      <div className="flex flex-col">
        {q.isLoading && (
          <div className="px-4 py-8 text-center text-secondary text-[13px]">
            Loading…
          </div>
        )}

        {!q.isLoading && (!report || report.roots.length === 0) && (
          <div className="px-4 py-8 text-center text-secondary text-[13px]">
            <p className="text-primary">No workspace roots configured.</p>
            <p className="mt-1 text-tertiary text-[12px]">
              Add roots in this device's <code>paperclip.toml</code> so the
              CEO can dispatch work into specific folders. See the docs for
              the <code>[workspaces]</code> section.
            </p>
          </div>
        )}

        {!q.isLoading && report && report.roots.length > 0 && (
          <>
            {grouped.map(({ root, items }) => (
              <div key={root} className="flex flex-col">
                <div className="border-b border-border-subtle bg-sunken px-4 py-1.5 text-[11px] text-tertiary font-mono">
                  {root}
                </div>
                {items.length === 0 ? (
                  <div className="border-b border-border-subtle px-4 py-3 text-[12px] text-tertiary">
                    No folders under this root.
                  </div>
                ) : (
                  <ul>
                    {items.map((w) => {
                      const isSelected = selected === w.path;
                      return (
                        <li key={w.path}>
                          <button
                            type="button"
                            onClick={() => onRowClick(w)}
                            className={`flex w-full items-center gap-3 border-b border-border-subtle px-4 py-2.5 text-left hover:bg-raised ${
                              isSelected
                                ? "border-l-2 border-l-accent bg-raised"
                                : ""
                            }`}
                            aria-pressed={isSelected}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-[13px] font-semibold text-primary truncate">
                                  {w.name}
                                </span>
                                {w.is_git_repo && (
                                  <span className="badge badge--accent">
                                    <GitBranch
                                      size={10}
                                      aria-hidden="true"
                                    />
                                    {w.branch ?? "git"}
                                  </span>
                                )}
                              </div>
                              <div className="text-[11px] text-tertiary font-mono truncate">
                                {w.path}
                              </div>
                            </div>
                            <span className="micro shrink-0">
                              {formatRelative(w.mtime)}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            ))}
          </>
        )}
      </div>
    </section>
  );
}

interface RootGroup {
  root: string;
  items: Workspace[];
}

function groupByRoot(
  report: { roots: string[]; workspaces: Workspace[] } | null,
): RootGroup[] {
  if (!report) return [];
  // Sort roots longest-first so a workspace below a nested root is bucketed
  // under the deepest matching root.
  const roots = [...report.roots].sort((a, b) => b.length - a.length);
  const groups: Record<string, Workspace[]> = Object.fromEntries(
    report.roots.map((r) => [r, [] as Workspace[]]),
  );
  const orphans: Workspace[] = [];
  for (const w of report.workspaces) {
    const match = roots.find((r) => isUnder(w.path, r));
    if (match) groups[match].push(w);
    else orphans.push(w);
  }
  // Sort items within each bucket by most-recently-touched.
  for (const k of Object.keys(groups)) {
    groups[k].sort((a, b) => b.mtime - a.mtime);
  }
  const out: RootGroup[] = report.roots.map((r) => ({
    root: r,
    items: groups[r],
  }));
  if (orphans.length)
    out.push({ root: "(unrooted)", items: orphans.sort((a, b) => b.mtime - a.mtime) });
  return out;
}

function isUnder(path: string, root: string): boolean {
  if (path === root) return true;
  // Tolerate both forward- and back-slash separators.
  const norm = (s: string) => s.replace(/\\/g, "/").replace(/\/+$/, "");
  const p = norm(path);
  const r = norm(root);
  return p === r || p.startsWith(r + "/");
}
